import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { PropertyResponse, UpdatePropertyBody } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"
import {
	canDeleteProperty,
	propertyDeleteApiPath,
} from "./property-delete-model"

export function PropertyEditPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const { profile } = useAuthenticatedUser()
	const memberships = usePropertyMemberships(id)
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [shareOpen, setShareOpen] = useState(false)

	const [name, setName] = useState("")
	const [description, setDescription] = useState("")
	const [initialized, setInitialized] = useState(false)
	const [formError, setFormError] = useState<string | null>(null)
	const [deleteError, setDeleteError] = useState<string | null>(null)
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

	const isOwner = memberships.data?.owner.id === profile.id
	const isArchitectMember = Boolean(
		memberships.data?.memberships.some(
			(m) => m.user.id === profile.id && m.role === "architect",
		),
	)
	const canEdit = isOwner || isArchitectMember
	const canDelete = canDeleteProperty({ isOwner })

	if (!isLoading && property && !initialized) {
		setName(property.name)
		setDescription(property.description ?? "")
		setInitialized(true)
	}

	const mutation = useMutation({
		mutationFn: (body: UpdatePropertyBody) =>
			apiFetch<PropertyResponse>(`/api/common/properties/${id}`, {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
		onSuccess: (res) => {
			// useProperty caches the unwrapped property — match that shape here so
			// the detail page reads the fresh values without a refetch.
			queryClient.setQueryData(
				queryKeys.property(res.property.id),
				res.property,
			)
			queryClient.invalidateQueries({ queryKey: queryKeys.properties() })
			navigate(webPaths.property(res.property.id))
		},
		onError: (err) => {
			setFormError(
				err instanceof ApiError
					? err.message
					: t.properties.form.errors.unknown,
			)
		},
	})

	const deleteMutation = useMutation({
		mutationFn: () =>
			id
				? apiFetch<void>(propertyDeleteApiPath(id), { method: "DELETE" })
				: Promise.resolve(),
		onSuccess: () => {
			if (id) queryClient.removeQueries({ queryKey: queryKeys.property(id) })
			queryClient.invalidateQueries({ queryKey: queryKeys.properties() })
			navigate(webPaths.dashboard(), { replace: true })
		},
		onError: (err) => {
			setDeleteError(
				err instanceof ApiError ? err.message : t.properties.edit.deleteError,
			)
		},
	})

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)
	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading || !property) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}

	const dirty =
		name.trim() !== property.name ||
		description.trim() !== (property.description ?? "")
	const tEdit = t.properties.edit

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer variant="narrow">
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<h1 className="text-2xl font-bold text-foreground">
					{tEdit.title.replace("{propertyName}", property.name)}
				</h1>
				{!canEdit ? (
					<p className="mt-6 text-sm text-muted-foreground">
						{tEdit.readOnlyNotice}
					</p>
				) : (
					<form
						className="mt-6 space-y-4"
						onSubmit={(e) => {
							e.preventDefault()
							setFormError(null)
							const body: UpdatePropertyBody = {}
							if (name.trim() !== property.name) body.name = name.trim()
							if (description.trim() !== (property.description ?? "")) {
								body.description = description.trim()
							}
							if (Object.keys(body).length === 0) return
							mutation.mutate(body)
						}}
					>
						<label className="block">
							<span className="block text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-1">
								{tEdit.nameLabel}
							</span>
							<input
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								maxLength={120}
								required
								className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							/>
						</label>
						<label className="block">
							<span className="block text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-1">
								{tEdit.descriptionLabel}
							</span>
							<textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								maxLength={2000}
								rows={5}
								className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							/>
						</label>
						{formError ? (
							<p role="alert" className="text-sm text-destructive">
								{formError}
							</p>
						) : null}
						<div className="flex items-center gap-2">
							<Button
								type="submit"
								variant="brand"
								size="default"
								disabled={!dirty || mutation.isPending}
							>
								{mutation.isPending ? tEdit.saving : tEdit.save}
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="default"
								onClick={() => navigate(webPaths.property(property.id))}
							>
								{tEdit.cancel}
							</Button>
						</div>
					</form>
				)}
				{canDelete ? (
					<section className="mt-10 border-t border-border pt-6">
						<h2 className="text-base font-semibold text-destructive">
							{tEdit.deleteSectionTitle}
						</h2>
						<p className="mt-2 text-sm text-muted-foreground">
							{tEdit.deleteSectionBody}
						</p>
						{deleteError ? (
							<p role="alert" className="mt-3 text-sm text-destructive">
								{deleteError}
							</p>
						) : null}
						<Button
							type="button"
							variant="destructive"
							size="default"
							className="mt-4"
							onClick={() => {
								setDeleteError(null)
								setDeleteConfirmOpen(true)
							}}
							disabled={deleteMutation.isPending}
						>
							{tEdit.deleteAction}
						</Button>
					</section>
				) : null}
			</PageContainer>
			{deleteConfirmOpen ? (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div
						role="dialog"
						aria-modal="true"
						aria-labelledby="property-delete-title"
						className="w-full max-w-sm rounded-lg border border-border bg-background p-5 shadow-xl"
					>
						<h2
							id="property-delete-title"
							className="text-lg font-semibold text-foreground"
						>
							{tEdit.deleteConfirmTitle}
						</h2>
						<p className="mt-3 text-sm text-muted-foreground">
							{tEdit.deleteConfirmBody.replace("{propertyName}", property.name)}
						</p>
						<div className="mt-5 flex justify-end gap-2">
							<Button
								type="button"
								variant="ghost"
								onClick={() => setDeleteConfirmOpen(false)}
								disabled={deleteMutation.isPending}
							>
								{tEdit.cancel}
							</Button>
							<Button
								type="button"
								variant="destructive"
								onClick={() => deleteMutation.mutate()}
								disabled={deleteMutation.isPending}
							>
								{tEdit.deleteConfirmAction}
							</Button>
						</div>
					</div>
				</div>
			) : null}
			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
		</div>
	)
}
